// packages/frontend/src/utils/formatters.js

/**
 * Format currency values consistently across the app.
 * - Defaults to EUR if no currency provided.
 * - Accepts number or numeric-like string; returns '' on invalid input.
 * - Never throws: falls back to "<amount> <CURRENCY>" when Intl fails.
 *
 * @param {number|string} amount
 * @param {string} [currency='EUR']  e.g., 'EUR', 'USD'
 * @param {string} [locale='en-US']
 * @returns {string}
 */
export function formatCurrency(amount, currency = 'EUR', locale = 'en-US') {
  if (amount == null) return '';
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(n)) return '';

  const code = String(currency || 'EUR').toUpperCase();

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      // Let Intl choose correct fraction digits for the currency.
      // If you want to force 2 dp for fiat, uncomment below:
      // minimumFractionDigits: 2,
    }).format(n);
  } catch {
    // Very rare (unknown currency/locale): safe fallback
    return `${n} ${code}`;
  }
}

/**
 * Format timestamps in seconds to "mm:ss" or "hh:mm:ss".
 * @param {number} seconds
 * @returns {string}
 */
export function formatTimestamp(seconds) {
  const sNum = Number(seconds);
  if (!Number.isFinite(sNum) || sNum < 0) return '0:00';

  const s = Math.floor(sNum % 60);
  const m = Math.floor((sNum / 60) % 60);
  const h = Math.floor(sNum / 3600);
  const pad = (n) => String(n).padStart(2, '0');

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Format dates (e.g., transactions, unlock times).
 * Defaults to a short, locale-aware format.
 *
 * @param {Date|string|number} date
 * @param {string} [locale='en-US']
 * @param {Intl.DateTimeFormatOptions} [opts={}]
 * @returns {string}
 */
export function formatDate(date, locale = 'en-US', opts = {}) {
  if (!date && date !== 0) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...opts,
    }).format(d);
  } catch {
    // Fallback ISO-like
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Shorten long strings (emails, IDs, product IDs, wallet addresses).
 * @param {string} text
 * @param {number} [start=6]
 * @param {number} [end=4]
 * @returns {string}
 */
export function shorten(text, start = 6, end = 4) {
  if (!text) return '';
  const s = String(text);
  if (s.length <= start + end) return s;
  return `${s.slice(0, start)}…${s.slice(-end)}`;
}

/**
 * Format a user display label.
 * Identity must be AgeVerif Sub (ageverifsub). Email is *not* identity,
 * it’s the Unlock recipient. This keeps UI consistent with audit rules.
 *
 * Priority:
 * 1) If `preferEmail` and email exists → show email (e.g., on checkout).
 * 2) AgeVerif sub (masked by default, use `maskAgeverifSub=false` to show full).
 * 3) Telegram username → @username
 * 4) Telegram id → TG#<short>
 * 5) userId fallback (shortened)
 *
 * @param {object} p
 * @param {string} [p.email]
 * @param {{ username?: string, id?: string|number }} [p.telegramUser]
 * @param {string|number} [p.userId]
 * @param {string} [p.ageverifsub]
 * @param {boolean} [p.preferEmail=false]           // e.g. on payment screens
 * @param {boolean} [p.maskAgeverifSub=true]        // mask the sub by default
 * @returns {string}
 */
export function formatUserDisplay({
  email,
  telegramUser,
  userId,
  ageverifsub,
  preferEmail = false,
  maskAgeverifSub = true,
} = {}) {
  // For checkout/Unlock copy we often prefer showing the email recipient.
  if (preferEmail && email) return String(email);

  if (ageverifsub) {
    const sub = String(ageverifsub);
    return maskAgeverifSub ? `AV#${shorten(sub, 6, 6)}` : `AV:${sub}`;
  }

  if (telegramUser?.username) return `@${telegramUser.username}`;
  if (telegramUser?.id != null) return `TG#${shorten(String(telegramUser.id), 3, 3)}`;
  if (userId != null) return shorten(String(userId), 4, 4);
  return 'Unknown user';
}
